/**
 * The models reply in light markdown — bold, bullets, blank-line paragraphs.
 * Rather than pull in a parser, this walks the text once and returns React
 * nodes, which also means nothing is ever dangerously set as HTML.
 *
 * It also picks out the things a revenue brief is actually read for — money,
 * dates and day counts — and tags them so the stylesheet can make them
 * findable at a glance instead of leaving a wall of grey sentences.
 */

/* Any three-letter currency followed by a figure: AED 22,920, USD 1,500.00. */
const MONEY = /\b[A-Z]{3}\s?\d[\d,]*(?:\.\d{1,2})?\b/;
/* 2026-08-16, and "August 16, 2026" / "16 Aug 2026". */
const DATE = new RegExp(
  '\\b\\d{4}-\\d{2}-\\d{2}\\b'
  + '|\\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\\s+\\d{1,2},?\\s+\\d{4}\\b'
  + '|\\b\\d{1,2}\\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\\s+\\d{4}\\b'
);
/* "19 days", "in 5 days", "22d". */
const DAYS = /\b\d+\s?(?:days?|d)\b(?!\w)/;
const BOLD = /\*\*(.+?)\*\*/;

/** Earliest match across the inline patterns, so they cannot overlap. */
function nextToken(text) {
  const tries = [
    ['bold', BOLD], ['money', MONEY], ['date', DATE], ['days', DAYS]
  ];
  let best = null;
  for (const [kind, re] of tries) {
    const m = text.match(re);
    if (m && (!best || m.index < best.m.index)) best = { kind, m, re };
  }
  return best;
}

/** One line of text as React nodes, with the interesting parts tagged. */
function inline(line, keyBase) {
  const out = [];
  let rest = String(line);
  let i = 0;

  while (rest) {
    const hit = nextToken(rest);
    if (!hit) { out.push(rest); break; }
    if (hit.m.index > 0) out.push(rest.slice(0, hit.m.index));

    const key = `${keyBase}-${i++}`;
    if (hit.kind === 'bold') out.push(<b key={key}>{hit.m[1]}</b>);
    else out.push(<span key={key} className={`md${hit.kind}`}>{hit.m[0]}</span>);

    rest = rest.slice(hit.m.index + hit.m[0].length);
  }
  return out;
}

/* A line that is nothing but bold text is a section heading: "Do these
   first:", "Worth knowing", or an owner's name in the team digest. */
const HEADING = /^\s*\*\*(.+?)\*\*\s*:?\s*$/;
/* "**Single Biggest Risk:** the rest of the sentence" reads as a callout. */
const CALLOUT = /^\s*\*\*(.+?:)\*\*\s*(.+)$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;

export function Markdown({ text }) {
  if (!text) return null;
  const blocks = [];
  let bullets = [];
  let seenBody = false;

  const flush = () => {
    if (!bullets.length) return;
    blocks.push(<ul key={`ul-${blocks.length}`}>{bullets}</ul>);
    bullets = [];
  };

  String(text).split('\n').forEach((line, i) => {
    const bullet = line.match(BULLET);
    if (bullet) {
      bullets.push(<li key={`li-${i}`}>{inline(bullet[1], `li-${i}`)}</li>);
      return;
    }

    flush();
    if (!line.trim()) return;

    const heading = line.match(HEADING);
    if (heading) {
      blocks.push(<h4 className="mdh" key={`h-${i}`}>{heading[1].replace(/:$/, '')}</h4>);
      return;
    }

    const callout = line.match(CALLOUT);
    if (callout) {
      blocks.push(
        <p className="mdnote" key={`n-${i}`}>
          <b>{callout[1]}</b> {inline(callout[2], `n-${i}`)}
        </p>
      );
      return;
    }

    // The opening paragraph is the standing summary; it leads the card.
    const lead = !seenBody;
    seenBody = true;
    blocks.push(
      <p className={lead ? 'mdlead' : undefined} key={`p-${i}`}>{inline(line, `p-${i}`)}</p>
    );
  });

  flush();
  return <>{blocks}</>;
}

export default Markdown;
